import React, { createContext, useCallback, useEffect, useState } from "react";
import useAuth from "../hooks/useAuth";
import axios from "../utils/bxApi";

export type ClientUIComponentType =
    "column"
    | "gridcolumn"
    | "numbercolumn"
    | "datecolumn"
    | "datepicker"
    | "combobox"
    | "listbox"
    | null;

export type Screen =
    "labellist"
    | "labelprintdialog"
    | "locationslabellist"
    | "pickingmonitor"
    | "receivingmonitor"
    | "webshopproducts"
    | "webshopbasket"
    | "checkout"
    | "orderhistory"
    | "assetsmanagerlist"
    ;

export interface ClientUI {
    bold: boolean | null,
    clientprofile: number | null,
    componenttype: ClientUIComponentType,
    dataformat: string,
    datatype: string | null,
    enabled: boolean,
    filtersource: null,
    fontsize: number | null,
    force: boolean | null,
    height: number | null,
    id: number,
    name: string,
    parentname: string | null,
    readonly: boolean,
    required: boolean,
    rightadjusted: boolean,
    screen: Screen,
    sourcefield: string,
    sourceobject: string | null,
    static: string | null,
    tabindex: number | null,
    tabstop: number | null,
    text: string,
    transparentbackground: boolean,
    value: string,
    visible: boolean,
    width: number | null,
    x: number | null,
    y: number | null
}

export interface ClientUIContextProps {
    getClientUI: (screen: Screen, name: string) => ClientUI | undefined,
    getClientUIs: (screen: Screen, name?: string) => ClientUI[],
}

type UseClientUIManager = ClientUIContextProps;

const useClientUIManager = (): ClientUIContextProps => {
    const { companyCode, isAuthenticated } = useAuth();
    const [clientUI, setClientUI] = useState<ClientUI[]>([]);

    const getClientUIs = useCallback((screen: Screen, name?: string) => clientUI.filter((record) => {
        if (!record.enabled || record.screen !== screen) {
            return false;
        }
        return !(name && name !== record.name);
    }), [clientUI])

    const getClientUI = useCallback((screen: Screen, name: string) => clientUI.find((record) => !(!record.enabled || record.screen !== screen || record.name.toLowerCase() !== name.toLowerCase())), [clientUI])

    useEffect(() => {
        let stillActive = true;
        const fetchClientUIFromServer = async () => {
            try {
                const { data } = await axios.get(`/${companyCode}/1.0/clientui`);

                if (stillActive) {
                    setClientUI(data.data);
                }
            } catch (err) {
                console.error(err);
            }
        }

        if (isAuthenticated && companyCode) {
            fetchClientUIFromServer();
        }

        return () => {
            stillActive = false;
        }
    }, [companyCode, isAuthenticated, setClientUI])

    return { getClientUIs, getClientUI }
}

export const ClientUIContext = createContext<UseClientUIManager>({
    getClientUIs: () => [],
    getClientUI: () => undefined
})

export const ClientUIProvider: React.FunctionComponent<{
    children: React.ReactNode
}> = ({ children }) => (
    <ClientUIContext.Provider value={useClientUIManager()}>{children}</ClientUIContext.Provider>
)
